/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import IScene from "./scene_interface";
import {RenderDocument, Canvas, CanvasTextGenerator, Debug, Messenger, StateViewer} from "js_renderer";
import GeoPathsManager from "./geo_paths_manager";
import Document from "./structural_elements/document";
import Listener from "./listener";
import {vec3} from "gl-matrix";
import IDocument from "./structural_elements/document_interface";
import IAbstractModule from "./modules/abstract_module_interface";
import Globe from "./globe/globe";
import GeoPoint from "./elements/geo_point";
import Line from "./elements/line";
import Terrain from "./terrain/terrain";
import IActivityModule from "./modules/activity/scene_activity_interface";
import IViewModule from "./modules/view/scene_view_interface";
import IRenderSettingsModule from "./modules/render_settings/scene_render_settings_interface";
import ILogoModule from "./modules/logo/scene_logo_interface";
import IManipulatorModule from "./modules/manipulators/scene_manipulator_interface";
import IBackgroundModule from "./modules/background/scene_background_interface";
import IGroundPlaneModule from "./modules/ground_plane/scene_ground_plane_interface";
import IAnimatorModule from "./modules/animations/scene_animator_interface";
import IAnnotationsModule from "./modules/annotations/scene_annotations_interface";
import IEffectModule from "./modules/effects/scene_effect_interface";
import ILightingModule from "./modules/lighting/scene_lighting_interface";
import IPickingModule from "./modules/picking/scene_picking_interface";
import IShadowsModule from "./modules/shadows/scene_shadows_interface";
import IClippingPlaneModule from "./modules/clipping_plane/scene_clipping_plane_interface";
import IPrintModule from "./modules/print/scene_print_interface";
import IHWCameraModule from "./modules/hw_camera/scene_hw_camera_interface";
import IHWCameraSimpleModule from "./modules/hw_camera/scene_hw_camera_simple_interface";
import IApplicationModule from "./modules/application/scene_application_interface";
import IHUDModule from "./modules/hud/scene_hud_interface";
import Settings from "./modules/settings";
import IViewModeModule from "./modules/view_mode/scene_view_mode_interface";
import IViewControlsModule from "./modules/view_controls/scene_view_controls_interface";
import ISaveModule from "./modules/save/scene_save_interface";
import IGeoLocationModule from "./modules/geo_location/scene_geo_location_interface";
import ITotalStationModule from "./modules/total_station/scene_total_station_interface";

export default
class Scene implements IScene {

    private static _currentScript = document.currentScript;
    private static _registeredModules: { [key: string]: IAbstractModule } = {};

   // TODO: temporary here until proper solution will be done via getters
    public applicationModule: IApplicationModule;
    public logoModule: ILogoModule;
    public activityModule: IActivityModule;
    public backgroundModule: IBackgroundModule;
    public animatorModule: IAnimatorModule;
    public annotationsModule: IAnnotationsModule;
    public effectsModule: IEffectModule;
    public lightingModule: ILightingModule;
    public manipulatorModule: IManipulatorModule;
    public pickingModule: IPickingModule;
    public renderSettingsModule: IRenderSettingsModule;
    public shadowsModule: IShadowsModule;
    public viewModule: IViewModule;
    public viewModeModule: IViewModeModule;
    public viewControlsModule: IViewControlsModule;
    public clippingPlaneModule: IClippingPlaneModule;
    public hwcameraModule: IHWCameraModule;
    public hwcamerasimpleModule: IHWCameraSimpleModule;
    public printModule: IPrintModule;
    public groundPlaneModule: IGroundPlaneModule;
    public hudModule: IHUDModule;
    public saveModule: ISaveModule;
    public geoLocationModule: IGeoLocationModule;
    public totalStationModule: ITotalStationModule;

    public globe: Globe;

    private _targetTag: HTMLDivElement;
    private _glCanvasParentTag: HTMLDivElement;
    private _canvas: Canvas;
    private _overlaysWrapper: HTMLDivElement;
    private _sdocument: IDocument;
    private _modulesInitialized: boolean;
    private stateViewer: StateViewer;
    private canvasTextRenderer: CanvasTextGenerator;
    private _listeners: any;
    private _isMobile: boolean;
    private _sceneLoaded: boolean;
    private _geoPathsManager: GeoPathsManager;
    private _modules: { [key: string]: any } = {};
    private _modelesInitializedFromGlobalSettings: boolean;
    private _scriptServerUrl = "";

    constructor(aTargetTag: HTMLDivElement) {
        this._targetTag = aTargetTag;   // scene div
        this._glCanvasParentTag = null;
        this._canvas = null;
        this._overlaysWrapper = null;
        this._sdocument = null;

        this._modulesInitialized = false;

        this.stateViewer = new StateViewer();
        this.canvasTextRenderer = new CanvasTextGenerator(this);
        this._listeners = {};

        this._create();

        this._instantiateRegisteredModules();

        this._isMobile = false;

        this._sceneLoaded = false; // this indicate that data for all models are downloaded and at least once rendered

        this._geoPathsManager = new GeoPathsManager(this);

        this._modelesInitializedFromGlobalSettings = false;
        // this._debugVincecty();

        this._scriptServerUrl = this.getServerUrl();
    }

    public static registerSceneAPIModule(aModule: any) {
        Scene._registeredModules[aModule.getClassType()] = aModule;

        // temporary for keep thing working until we replace all modules access via getter
        this[aModule.getClassType() + "Module"] = aModule;
    }

    private static unRegisterSceneAPIModules() {
        Scene._registeredModules = {};
    }

    public getServerUrl(): string {
        if (this._scriptServerUrl === "") {
            this._initServerUrl();
        }
        return this. _scriptServerUrl;
    }

    // TB: nice, but this cannot be used imho
    // reason: circular dependencies
    // in SceneAPI modules we must reffer to other modules via interfaces
    // static method cannot be declared in interface
    public getModule(aModule: any): IAbstractModule {
        return this._modules[aModule.getClassType()];
    }

    public getModuleByType(aModule: string): any {
        // todo TB: some check?
        if (this._modules.hasOwnProperty(aModule)) {
            return this._modules[aModule];
        }
        Debug.logWarning("SceneAPI: Module '" + aModule + "' not found!");
        return null;
    }

    public getApplicationModule(): IApplicationModule {
        return this._modules["application"];
    }

    public getActivityModule(): IActivityModule {
        return this._modules["activity"];
    }

    public getViewModule(): IViewModule {
        return this._modules["view"];
    }

    public getBackgroundModule(): IBackgroundModule {
        return this._modules["background"];
    }

    public getLightingModule(): ILightingModule {
        return this._modules["lighting"];
    }

    public getRenderSettingsModule(): IRenderSettingsModule {
        return this._modules["renderSettings"];
    }

    public getShadowsModule(): IShadowsModule {
        return this._modules["shadows"];
    }

    public getTotalStationModule(): ITotalStationModule {
        return this._modules["totalStation"];
    }

    public release() {
        this._targetTag.innerHTML = "";

        this._sdocument.release();
        this._canvas.release();

        this._overlaysWrapper = null; // created x3d tag

        Scene.unRegisterSceneAPIModules();
    }

    public getGeoPathsManager(): GeoPathsManager {
        return this._geoPathsManager;
    }

    public getCanvasTextRenderer(): CanvasTextGenerator {
        return this.canvasTextRenderer;
    }

    public setSceneLoaded(aValue: boolean) {
        this._sceneLoaded = aValue;
    }

    public getSceneLoaded(): boolean {
        return this._sceneLoaded;
    }

    public getStateViewer(): StateViewer {
        return this.stateViewer;
    }

    public checkModulesInitialized(): boolean {
        return this._modulesInitialized;
    }

    /**
     * This function is called when the scene is attached to the application
     */
    public attachedToApplication() {
        if (!this._canvas.hasValidContext()) {
            return;
        }

        // The webgl context was created when the scene was instantiated, but the css canvas size seems to be not valid on iOS (bug?)
        // The problem is that resize is called correctly on desktops, but on iOS, we need to call the resize check manually.
        this._canvas.handleResize();

        // we need to ensure that at least one render is done (the viewMode have to be already set via canvas.setViewMode())
        // and cwx3d_engineIsReady listener have to be registered
        this._canvas.tick();
    }

    public getTargetTag(): HTMLDivElement {
        return this._targetTag;
    }

    public getCanvasParentTag(): HTMLDivElement {
        return this._glCanvasParentTag;
    }

    public getDocument(): IDocument {
        return this._sdocument;
    }

    /**
     * @returns {Canvas}
     */
    public getCanvas(): Canvas {
        return this._canvas;
    }

    public getHTMLCanvas(): HTMLCanvasElement {
        return this._canvas.getHTMLCanvas();
    }

    public getOverlaysWrapper(): HTMLDivElement {
        return this._overlaysWrapper;
    }

    public focus() {
        return this._canvas.focus();
    }

    public clear() {
        return this._sdocument.clear();
    }

    public applicationReady(aValue: boolean) {
        Messenger.setApplicationReady(aValue === undefined ? true : aValue);
    }

    public isMobile(): boolean {
        return this._isMobile;
    }

    public setIsMobile(aValue: boolean) {
        this._isMobile = aValue;
    }

    /**
     * Returns url to server lib dir (where 3rd party libraries are stored) according to debug flag set.
     * For debug on, localhost address is returned, otherwise public release server is returned.
     *
     * @returns {string} Url containing trailing "/".
     */
    public getServerLibsDirUrl(): string {
        // public release server
        // @ts-ignore still handled by old build system
        let url = cadwork.webgl.versionInfo.server + "/" + cadwork.webgl.versionInfo.deploy + "/jsInterface/lib/";

        // @ts-ignore
        if (cadwork._DEBUG) {
            // relative for localhost
            url = "../renderers/jsInterface/lib/";
        }

        Debug.logInfo(url);
        return url;
    }

    public addListener(aType: string, aCallback: any, aCaller: any): Listener
    {
        let listener = new Listener(this, aType, aCallback, aCaller);
        this._listeners[listener.getUID()] = listener;

        return listener;
    }

    public invokeListener(aType: string, aEvt: any)
    {
        let regListener;
        for (let key in this._listeners) {
            regListener = this._listeners[key];
            if (regListener.getType() === aType) {
                regListener.getCallback().call(regListener.getCaller(), aEvt);
            }
        }
    }

    public removeListener(aListener: Listener)
    {
        if (aListener === null || aListener === undefined) {
            return false;
        }

        if (this._listeners[aListener.getUID()] !== undefined) {
            delete this._listeners[aListener.getUID()];
        }

        return true;
    }

    /**
     * Function create sceneAPI element of type "Line", but do not add it to the rendering graph. This have to be done by calling drawLine() function.
     * @param {vec3} aPointA Start point of the line.
     * @param {vec3} aPointB End point of the line.
     */
    public createMeasureLine(aPointA: vec3, aPointB: vec3, aProperties: any)
    {
        if (aProperties !== undefined && aProperties !== null) {
            return this.createLine(aPointA, aPointB, aProperties);
        }
        else {
            // default properties
            return this.createLine(aPointA, aPointB, {notOutline: true, alwaysOnTop: true, pickable: false});
        }
    }

    /**
     * FUnction create sceneAPI element of type "Line", but do not add it to the rendering graph. This have to be done by calling drawLine() function.
     * @param aPointA Start point of the line in planet space.
     * @param aPointB End point of the line in planet space.
     */
    public createLine(aPointA: vec3, aPointB: vec3, aProperties: any)
    {
        let lineAPIElement: any = this._sdocument.createAPIElement(Line.TypeName);
        if (aProperties !== undefined && aProperties !== null) {
            lineAPIElement.extendProperties(aProperties);
        }

        this._sdocument.attachElementToInternalModel(lineAPIElement);
        lineAPIElement.setPositions(aPointA, aPointB);

        return lineAPIElement;
    }

    public createGeoPoint()
    {
        let geoPointAPIElement: any = this._sdocument.createAPIElement(GeoPoint.TypeName);
        this._sdocument.attachElementToInternalModel(geoPointAPIElement);

        return geoPointAPIElement;
    }

    public createGeoPath(aProperties)
    {
        return this._geoPathsManager.createGeoPath(aProperties);
    }

    /***
     * Extend the scene API with function that enable the app to create a terrain
     */
    public createGlobe(aRenderDocument: RenderDocument): Globe
    {
        return new Globe(aRenderDocument);
    }

    public createTerrain(): Terrain
    {
        return new Terrain(this);
    }

    /*
    public captureMouse(aCapture)
    {
        if (!this._isCreated) return false;

        let canvas = this._glCanvasTag.runtime.canvas;
        canvas.cdwk_captureMouse(aCapture);
    }
    */

    public setModulesFromModelDescription(aModelDecriptionJsonObject: any)
    {
        if (!aModelDecriptionJsonObject || this._modelesInitializedFromGlobalSettings) {
            return;
        }

        Settings.lock();
        for (let m in this._modules) {
            this._modules[m].setFromModelDescription(aModelDecriptionJsonObject);
        }
        Settings.unlock();
    }

    /**
     * Initialize scene api modules (create scene api elements needed) and their dependencies.
     * Should be called when settings is loaded and geometry is created.
     */
    public mainModelReady() {

        // apply loaded settings to all modules - initialize modules and their dependencies correctly
        for (let m in this._modules) {
            this._modules[m].set(this._modules[m].getModuleSettings(), true);
        }

        this.notifySettingsObservers();
    }

    /**
     * Pass modules' settings to its registered settings observers (application).
     * Initialize gui in fact.
     */
    public notifySettingsObservers()
    {
        for (let m in this._modules) {
            let module = this._modules[m];
            module.notifySettingsObservers();
        }
    }

    public getConfiguration(): any
    {
        let configuration = {};
        for (let m in this._modules) {
            let module = this._modules[m];
            let settings = module.getSettings();
            for (let key in settings) {
                if (Object.keys(settings[key]).length !== 0) {
                    configuration[key] = settings[key];
                }
            }
        }

        return configuration;
    }

    public setConfiguration(aConfigurationJson: any) {
        let configuration = aConfigurationJson;

        if (aConfigurationJson === undefined) { // local models
            // look to DOM for globalSettings script tag
            let globalSettings: HTMLElement = document.getElementById("globalSettings");
            if (!globalSettings) {
                // TODO: log sth?
                return;
            }
            else {
                configuration = JSON.parse(window.atob(globalSettings.getAttribute('base64data')));
                this.setConfigurationData(configuration);
            }
        }
        else { // TODO: xhr for url

            // expect json
            configuration = JSON.parse(window.atob(aConfigurationJson));
            this.setConfigurationData(configuration);
        }
    }

    private setConfigurationData(aConfigurationJson: any) {
        Settings.lock();
        for (let m in this._modules) {
            this._modules[m].set(aConfigurationJson[m]);
        }
        Settings.unlock();

        this._modelesInitializedFromGlobalSettings = true;
    }

    private _create() {
        this._targetTag.innerHTML = "\<x3d id='cadworkWebglCanvas'>\</x3d>";
        this._glCanvasParentTag = this._targetTag.firstChild as HTMLDivElement;

        this._overlaysWrapper = document.createElement('div');
        this._overlaysWrapper.setAttribute("id", "overlaysWrapper");
        this._glCanvasParentTag.appendChild(this._overlaysWrapper);

        // create screen space where we will render - canvas
        this._canvas = new Canvas(this, this._glCanvasParentTag);

        // create virtual space where we will render - document
        this._sdocument = new Document(this);

        // add the model properties viewer to the target tag and hide it
        // this._targetTag.appendChild(this.stateViewer.viewer);
        this._glCanvasParentTag.appendChild(this.stateViewer.viewer);

        // add debug window
        Debug.activate(false);
        this._targetTag.appendChild(Debug.logContainer);

        // add internal textures (like default points texture) to the DOM
    }

    private _instantiateRegisteredModules() {
        if (Scene._registeredModules === undefined) {
            return;
        }

        let register = (key: string) => {
            let module: any = Scene._registeredModules[key];
            if (this._modules[module.getClassType()] == undefined) {
                let moduleInstance = this._sdocument.iocSceneApiModule(module);
                this._modules[module.getClassType()] = moduleInstance;
                this[module.getClassType() + "Module"] = moduleInstance;
            }
        };

        // first register activity module as it is required by others
        register("activity");

        // ensure view mode is registered before view controls
        register("viewMode");

        for (let key in Scene._registeredModules) {
            register(key);
        }

        this._registerModulesDependencies();

        this._modulesInitialized = true;
    }

    private _registerModulesDependencies() {
        for (let m in this._modules) {
            this._modules[m].initDependencies();
        }
    }

    private _initServerUrl() {
        console.log('_initServerUrl');
        let scriptServerUrl: string = "";
        // TODO TB: release build and deploy build shall be the same so remove DEPLOY_BUILD var from ts scripts
        // when running at linux build server and using symlinks
///////////////////////////
///////////////////////////////////////////
/////////////////////////////
////////////////////////
/////////////////
//////////////////////////////////////////////////////////
///////////////////////////////////////
////////////////////////////////////////////////////////////////////////
/////////////
/////////////////////////
//////////////////////////////////////////////////////
/////////////////////////
//////////////////////////////////////////////////////////////////////////////
////////////////
//////////////////////////////////////////////////////////////////
/////////
////////////////
        scriptServerUrl = window.origin;
/////////////////

        this._scriptServerUrl = scriptServerUrl;
        console.log('this._scriptServerUrl: ' + this._scriptServerUrl);
    }

    private _debugVincecty() {
        /*let lat1d = Dms.parseDMS('35°58\'42.1"N');//.val();
        let lon1d = Dms.parseDMS('14°20\'11.14"E');//.val();
        let lat2d = Dms.parseDMS('35°59\'13.3"N');//.val();
        let lon2d = Dms.parseDMS('14°20\'49.0"E');//.val();

        let zeropointd = new LatLon(lat1d, lon1d, LatLon.datum.WGS84);

        // poitn at hotel sun shield 35.987038, 14.346940
        let xd = zeropointd.distanceTo(new LatLon(lat2d, lon1d, LatLon.datum.WGS84));
        let yd = zeropointd.distanceTo(new LatLon(lat1d, lon2d, LatLon.datum.WGS84));

        let lat1 = 35.978347;
        let lon1 = 14.336427;
        let lat2 = 35.987038;
        let lon2 = 14.346940;

        let zeropoint = new LatLon(lat1, lon1, LatLon.datum.WGS84);

        // poitn at hotel sun shield 35.987038, 14.346940
        let x = zeropoint.distanceTo(new LatLon(lat2, lon1, LatLon.datum.WGS84));
        let y = zeropoint.distanceTo(new LatLon(lat1, lon2, LatLon.datum.WGS84));

        let a = 1;*/
    }
}
